import { useState } from "react";
import { ImagePlus, Trash2 } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { api, fetchCollection, formatApiError } from "@/lib/api";

export function HeroImagesAdmin() {
  const queryClient = useQueryClient();
  const { data = [] } = useQuery({ queryKey: ["hero-images"], queryFn: () => fetchCollection("/hero-images") });
  const [uploading, setUploading] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["hero-images"] });

  const upload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const body = new FormData();
        body.append("file", file);
        body.append("title", file.name.replace(/\.[^.]+$/, ""));
        await api.post("/admin/hero-images", body, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} fotografía${files.length > 1 ? "s" : ""} añadida${files.length > 1 ? "s" : ""} a la portada`);
      refresh();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setUploading(false);
      event.target.value = "";
    }
  };

  const remove = useMutation({
    mutationFn: async (id) => (await api.delete(`/admin/hero-images/${id}`)).data,
    onSuccess: () => { toast.success("Fotografía eliminada"); refresh(); },
    onError: (error) => toast.error(formatApiError(error)),
  });

  return (
    <div className="grid gap-6" data-testid="admin-hero-panel">
      <div className="flex flex-col justify-between gap-4 border border-white/10 bg-white/[0.03] p-5 md:flex-row md:items-center">
        <div>
          <p className="eyebrow">Fotografías de portada · {data.length}</p>
          <p className="mt-2 text-sm text-slate-300">Cada visita a la portada muestra la siguiente fotografía de la lista, en orden. Sube tantas como quieras (JPG, PNG o WEBP, máx. 12 MB).</p>
        </div>
        <label className="inline-flex cursor-pointer items-center gap-2 border border-clay/40 bg-clay/10 px-5 py-3 font-mono text-xs uppercase tracking-[0.2em] text-clay transition-colors hover:bg-clay hover:text-charcoal" data-testid="admin-hero-upload-label">
          <input type="file" multiple accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="admin-hero-upload-input" />
          {uploading ? "Subiendo..." : "Añadir fotografías"} <ImagePlus className="h-4 w-4" />
        </label>
      </div>

      {data.length === 0 ? <p className="border border-dashed border-white/15 p-6 text-sm text-slate-500" data-testid="admin-hero-empty">Sin fotografías: la portada usará la imagen por defecto.</p> : null}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-hero-list">
        {data.map((item, index) => (
          <article key={item.id} className="poster-card group relative overflow-hidden" data-testid={`admin-hero-item-${item.id}`}>
            <img src={`${process.env.REACT_APP_BACKEND_URL}${item.url}`} alt={item.title || "Fotografía de portada"} className="h-44 w-full object-cover" />
            <div className="flex items-center justify-between gap-3 p-3">
              <p className="truncate font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400"><span className="text-clay">{String(index + 1).padStart(2, "0")}</span> · {item.title}</p>
              <Button type="button" variant="outline" onClick={() => window.confirm("¿Eliminar esta fotografía de la portada?") && remove.mutate(item.id)} disabled={remove.isPending} className="h-8 rounded-none border-red-500/25 bg-red-500/5 px-2 text-red-200 hover:bg-red-500/20" data-testid={`admin-hero-delete-${item.id}`}>
                <Trash2 className="h-3 w-3" />
              </Button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}
